/**
 * Centralized Typography System
 *
 * Base font size: 16px (standard for modern web)
 * Heading scales calculated proportionally from 16px base
 */

export const typography = {
  // Font families
  fontFamily: [
    'Arial',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'sans-serif',
  ].join(','),

  fontFamilyMonospace: [
    'Menlo',
    'Monaco',
    'Consolas',
    '"Courier New"',
    'monospace',
  ].join(','),

  // Base font size (standard web default)
  fontSize: 16,

  // Font weights
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,

  // Line heights
  lineHeight: 1.5,

  // Heading styles (proportional to 16px base)
  h1: {
    fontSize: 40,           // 16 * 2.5 = 40px
    fontWeight: 500,
    lineHeight: 1.2,
  },

  h2: {
    fontSize: 32,           // 16 * 2 = 32px
    fontWeight: 500,
    lineHeight: 1.3,
  },

  h3: {
    fontSize: 28,           // 16 * 1.75 = 28px
    fontWeight: 500,
    lineHeight: 1.4,
  },

  h4: {
    fontSize: 24,           // 16 * 1.5 = 24px
    fontWeight: 500,
    lineHeight: 1.4,
  },

  h5: {
    fontSize: 20,           // 16 * 1.25 = 20px
    fontWeight: 500,
    lineHeight: 1.5,
  },

  h6: {
    fontSize: 16,           // 16 * 1 = 16px
    fontWeight: 500,
    lineHeight: 1.6,
  },

  // Body text variants
  body1: {
    fontSize: 16,
    lineHeight: 1.5,
  },

  body2: {
    fontSize: 14,           // 16 * 0.875 = 14px
    lineHeight: 1.43,
  },

  // Button text
  button: {
    fontSize: 16,
    fontWeight: 400,
    textTransform: 'none' as const,
  },

  // Caption text
  caption: {
    fontSize: 12,
    lineHeight: 1.5,
  },

  // Overline text
  overline: {
    fontSize: 12,
    fontWeight: 400,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },

  // Font size variants
  sizes: {
    large: 20,              // 16 * 1.25
    base: 16,
    small: 14,              // 16 * 0.875
    tiny: 12,               // 16 * 0.75
  },
} as const;

export type Typography = typeof typography;
